import {
  signInWithEmailAndPassword,
  signInWithPopup,
  TwitterAuthProvider,
  signOut,
  onAuthStateChanged,
  User as FirebaseUser,
} from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from './firebase';
import { User } from '../types/content';
import { twitterAuthService, TwitterAuthData, SIGN_IN_REDIRECTING } from './twitter-auth-service';
import { computeTwitterIdentityPatch } from './twitter-identity-patch';

export class AuthService {
  private getAuth() {
    if (!auth) {
      throw new Error('Auth not initialized');
    }
    return auth;
  }

  private getDb() {
    if (!db) {
      throw new Error('Database not initialized');
    }
    return db;
  }

  // Sign in with email and password
  async signIn(email: string, password: string): Promise<User | null> {
    try {
      const firebaseAuth = this.getAuth();
      const userCredential = await signInWithEmailAndPassword(firebaseAuth, email, password);
      const firebaseUser = userCredential.user;
      
      // Get or create user document in Firestore
      const userData = await this.getOrCreateUserDocument(firebaseUser);
      
      if (userData) {
        // Check and update email verification status
        await this.updateEmailVerificationStatus(firebaseUser, userData);
        
        // Update last login time
        await this.updateLastLogin(userData.id!);
        
        // Get updated user data after potential verification status change
        const updatedUserData = await this.getUserData(userData.id!);
        return updatedUserData;
      }
      
      return userData;
    } catch (error) {
      // Sign in error - handle silently in production
      throw error;
    }
  }

  // Sign in with Twitter OAuth.
  // Returns SIGN_IN_REDIRECTING when the mobile popup fallback kicked in — the page is
  // navigating away and no user resolves here; completeTwitterRedirectSignIn() (run on the
  // return load) finishes it. Callers must handle the sentinel, not treat it as a null user.
  async signInWithTwitter(): Promise<User | typeof SIGN_IN_REDIRECTING | null> {
    try {
      const result = await twitterAuthService.signInWithTwitter();

      if (result === SIGN_IN_REDIRECTING) {
        return SIGN_IN_REDIRECTING;
      }

      const { firebaseUser, twitterData } = result;

      // Get or create user document in Firestore
      const userData = await this.getOrCreateUserDocument(firebaseUser, twitterData);

      if (userData) {
        // Update last login time
        await this.updateLastLogin(userData.id!);

        // Get updated user data
        const updatedUserData = await this.getUserData(userData.id!);
        return updatedUserData;
      }

      return userData;
    } catch (error) {
      console.error('Twitter sign-in error:', error);
      throw error;
    }
  }

  // Complete a Twitter sign-in that fell back to a full-page redirect (mobile resilience).
  // Runs on page load from the AuthProvider. For a BRAND-NEW contributor this CREATES the
  // Firestore user doc — a bare onAuthStateChanged read would miss it and strand the user
  // signed-in-but-doc-less.
  //
  // Distinguishes the two outcomes so the caller can tell silence from failure:
  //   - null  → NO redirect pending (the common case on a normal page load) — stay silent.
  //   - throw → a redirect WAS returning but FAILED (e.g. getRedirectResult rejected). The
  //             mount effect surfaces this to the user; a swallowed error would strand the
  //             contributor back on the page with nothing happening and no message.
  // The AuthProvider wraps this call so a throw never blocks onAuthStateChanged from wiring up.
  async completeTwitterRedirectSignIn(): Promise<User | null> {
    const redirect = await twitterAuthService.getRedirectSignInResult();
    if (!redirect) {
      return null;
    }

    const { firebaseUser, twitterData } = redirect;
    const userData = await this.getOrCreateUserDocument(firebaseUser, twitterData);

    if (userData) {
      await this.updateLastLogin(userData.id!);
      return await this.getUserData(userData.id!);
    }

    return userData;
  }

  // Sign out
  async signOut(): Promise<void> {
    try {
      const firebaseAuth = this.getAuth();
      await signOut(firebaseAuth);
    } catch (error) {
      // Sign out error - handle silently in production
      throw error;
    }
  }

  // Get current user
  getCurrentUser(): Promise<FirebaseUser | null> {
    return new Promise((resolve) => {
      const firebaseAuth = this.getAuth();
      const unsubscribe = onAuthStateChanged(firebaseAuth, (user) => {
        unsubscribe();
        resolve(user);
      });
    });
  }

  // Get user data from Firestore
  async getUserData(uid: string): Promise<User | null> {
    try {
      const database = this.getDb();
      const userDoc = await getDoc(doc(database, 'users', uid));
      if (userDoc.exists()) {
        const data = userDoc.data();
        return {
          id: userDoc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          lastLoginAt: data.lastLoginAt?.toDate(),
          twitterUsernameLastUpdatedAt: data.twitterUsernameLastUpdatedAt?.toDate(),
          applicationSubmittedAt: data.applicationSubmittedAt?.toDate(),
          emailVerificationCompletedAt: data.emailVerificationCompletedAt?.toDate(),
          lastActivityAt: data.lastActivityAt?.toDate(),
          deletedAt: data.deletedAt?.toDate(),
        } as User;
      }
      return null;
    } catch (error) {
      // Error getting user data - handle silently in production
      return null;
    }
  }

  // Get or create user document in Firestore
  private async getOrCreateUserDocument(
    firebaseUser: FirebaseUser,
    twitterData?: TwitterAuthData
  ): Promise<User | null> {
    try {
      const database = this.getDb();
      const userDocRef = doc(database, 'users', firebaseUser.uid);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        const data = userDoc.data();

        // Refresh the volatile Twitter identity on EVERY sign-in (not just first-link). X
        // avatars (pbs.twimg.com) and display names/handles rot; a doc that predates the
        // Twitter fields (or holds a stale avatar) never self-heals otherwise — the literal
        // cause of "NO PROFILE IMAGE" for pre-existing accounts. computeTwitterIdentityPatch
        // returns the MINIMAL changed-keys patch (empty when nothing moved → skip the write,
        // Firestore cost discipline) and never clobbers a user-chosen profileImage.
        if (twitterData) {
          const patch = computeTwitterIdentityPatch(data, twitterData);
          if (Object.keys(patch).length > 0) {
            await updateDoc(userDocRef, patch);
            Object.assign(data, patch); // reflect fresh values in the returned User
          }
        }

        return {
          id: userDoc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          lastLoginAt: data.lastLoginAt?.toDate(),
          twitterUsernameLastUpdatedAt: data.twitterUsernameLastUpdatedAt?.toDate(),
          applicationSubmittedAt: data.applicationSubmittedAt?.toDate(),
          emailVerificationCompletedAt: data.emailVerificationCompletedAt?.toDate(),
          lastActivityAt: data.lastActivityAt?.toDate(),
          deletedAt: data.deletedAt?.toDate(),
        } as User;
      } else {
        // Create new user document
        let newUser: Omit<User, 'id'>;
        
        if (twitterData) {
          // Create user from Twitter OAuth data.
          // FIRST-LOGIN DEFAULT (WS-K): brand-new Twitter users get the user-world
          // 'contributor' role — NOT 'writer' (frozen), 'editor', or 'guest'. This branch
          // ONLY runs when no users/{uid} doc exists, so existing admins/editors/writers
          // keep their stored role untouched (their doc already exists → the if-branch above).
          // requireRole(user, [UserRole.CONTRIBUTOR, ...]) works server-side against this value.
          newUser = {
            email: twitterData.email || undefined,
            displayName: twitterData.twitterDisplayName || 'Contributor',
            role: 'contributor', // user-world default; was 'writer' (now frozen)
            isActive: false, // New Twitter users start inactive until approved by admin
            applicationSubmitted: false, // Explicitly set to false for new users
            createdAt: new Date(),
            authProvider: 'twitter',
            twitterId: twitterData.twitterId,
            twitterUsername: twitterData.twitterUsername,
            twitterDisplayName: twitterData.twitterDisplayName,
            twitterProfileImageUrl: twitterData.twitterProfileImageUrl,
            profileImage: twitterData.twitterProfileImageUrl,
          };
        } else {
          // Create user from email/password. This is the LEGACY staff/dev path — email/
          // password accounts are provisioned manually and their role is set in Firestore.
          // Left as 'writer' for backward compatibility (the public signup surface is
          // Twitter, which now defaults to 'contributor'). admin/owner are unchanged.
          newUser = {
            email: firebaseUser.email || '',
            displayName: firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'Writer',
            role: 'writer', // Legacy default for the manual email/password path
            isActive: true,
            createdAt: new Date(),
            authProvider: 'email',
          };
        }

        await setDoc(userDocRef, {
          ...newUser,
          createdAt: serverTimestamp(),
        });

        return {
          id: firebaseUser.uid,
          ...newUser,
        };
      }
    } catch (error) {
      console.error('Error in getOrCreateUserDocument:', error);
      return null;
    }
  }

  // Update last login time
  private async updateLastLogin(uid: string): Promise<void> {
    try {
      const database = this.getDb();
      const userDocRef = doc(database, 'users', uid);
      await updateDoc(userDocRef, {
        lastLoginAt: serverTimestamp(),
      });
    } catch (error) {
      // Error updating last login - handle silently in production
    }
  }

  // Update email verification status based on Firebase Auth status
  private async updateEmailVerificationStatus(firebaseUser: any, userData: User): Promise<void> {
    try {
      const database = this.getDb();
      // Check if user has pending email verification and is now verified
      if (userData.pendingEmailVerification && firebaseUser.emailVerified) {
        const userDocRef = doc(database, 'users', userData.id!);
        await updateDoc(userDocRef, {
          emailVerified: true,
          isActive: true,
          pendingEmailVerification: false,
          emailVerificationCompletedAt: serverTimestamp(),
        });
        }
      // Update emailVerified status even if not pending (for existing users)
      else if (userData.emailVerified !== firebaseUser.emailVerified) {
        const userDocRef = doc(database, 'users', userData.id!);
        await updateDoc(userDocRef, {
          emailVerified: firebaseUser.emailVerified,
        });
      }
    } catch (error) {
      }
  }

  // Check if user has required permissions
  async checkUserPermissions(uid: string, requiredRole: 'writer' | 'admin' = 'writer'): Promise<boolean> {
    const userData = await this.getUserData(uid);
    
    if (!userData || !userData.isActive) {
      return false;
    }

    if (requiredRole === 'admin') {
      return userData.role === 'admin';
    }

    return userData.role === 'writer' || userData.role === 'admin';
  }
}

// Export service instance
export const authService = new AuthService();

